/**
 * useCmsContent — module-level cache + React hook that fetches /api/cms/content
 * once per session. Public landing page sections + experience-detail pages
 * call this to read admin-managed copy without each section re-fetching.
 *
 * Backwards compatible: when content is null (still loading or API fails),
 * components fall back to their hardcoded defaults so the page never breaks.
 */
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

let _cache = null;          // last successful payload
let _inflight = null;       // current Promise (so concurrent hooks share it)
let _ts = 0;
const TTL_MS = 60 * 1000;   // mirror server's 60s cache

const fetchContent = (force = false) => {
  const now = Date.now();
  if (!force && _cache && now - _ts < TTL_MS) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = fetch(`${API_URL}/api/cms/content`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((data) => {
      _cache = data || {};
      _ts = Date.now();
      _inflight = null;
      return _cache;
    });
  return _inflight;
};

export const invalidateCmsCache = () => {
  _cache = null;
  _ts = 0;
};

// --- Cross-tab live sync -------------------------------------------------
// When an admin saves landing content in the CRM tab, ping every other tab
// (same origin) so the public landing page refetches instantly — no reload.
const CMS_CHANNEL = 'cms_updates';

export const broadcastCmsUpdate = () => {
  try { const bc = new BroadcastChannel(CMS_CHANNEL); bc.postMessage({ t: Date.now() }); bc.close(); } catch { /* unsupported */ }
  try { localStorage.setItem('cms_updated_at', String(Date.now())); } catch { /* private mode */ }
};

export const subscribeCmsUpdate = (cb) => {
  let bc;
  try { bc = new BroadcastChannel(CMS_CHANNEL); bc.onmessage = () => cb(); } catch { /* unsupported */ }
  const onStorage = (e) => { if (e.key === 'cms_updated_at') cb(); };
  window.addEventListener('storage', onStorage);
  return () => {
    try { if (bc) bc.close(); } catch { /* noop */ }
    window.removeEventListener('storage', onStorage);
  };
};

export const useCmsContent = () => {
  const [content, setContent] = useState(_cache);
  useEffect(() => {
    let cancelled = false;
    fetchContent().then((d) => { if (!cancelled) setContent(d); });
    const unsub = subscribeCmsUpdate(() => {
      invalidateCmsCache();
      fetchContent(true).then((d) => { if (!cancelled) setContent(d); });
    });
    return () => { cancelled = true; unsub(); };
  }, []);
  return content;
};

export const useCmsSection = (key, fallback = {}) => {
  const c = useCmsContent();
  if (!c || !c[key]) return fallback;
  return { ...fallback, ...c[key] };
};

export default useCmsContent;
